// import logo from './logo.svg';
import './App.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { Toaster } from 'react-hot-toast';
import DashboardPage from './pages/DashboardPage';
import TokenPage from './pages/TokenPage';
import StocksDashboardPage from './pages/StocksDashboardPage';
import StockPage from './pages/StockPage';
import StockPositionsPage from './pages/StockPositionsPage';
import PositionsPage from './pages/PositionsPage';
import TransactionsPage from './pages/TransactionsPage';
import ImportsPage from './pages/ImportsPage';
import ImportsReviewPage from './pages/ImportsReviewPage';
import ImportResultPage from './pages/ImportResultPage';
import AllocationPlannerPage from './pages/AllocationPlannerPage';
import StockAllocationPlannerPage from './pages/StockAllocationPlannerPage';
import { STOCK_ALLOCATIONS_ROUTE } from './constants/routes';
import GroupsPage from './pages/GroupsPage';
// import TokensPage from './pages/TokensPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import AccountSecurityPage from './pages/AccountSecurityPage';
import ApiKeysPage from './pages/ApiKeysPage';
import PasswordResetPage from './pages/PasswordResetPage';
import LendingCheckPage from './pages/LendingCheckPage';
import ExitsPage from './pages/ExitsPage';
import StrategiesPage from './pages/StrategiesPage';
import ExportPage from './pages/ExportPage';
import TagsPage from './pages/TagsPage';

import Header from './components/Header';
import MarketIndicators from './components/MarketIndicators';
import client from './services/api';
import { TradingViewProvider } from './components/TradingViewChart';
import { ThemeProvider } from './components/ThemeProvider';
import AuthWrapper from './components/AuthWrapper';
import { AuthProvider } from './contexts/AuthContext';
import { PortfolioProvider } from './contexts/PortfolioContext';
import { PositionProvider } from './contexts/PositionContext';
import { ImportModalProvider } from './contexts/ImportModalContext';
import { JobNotificationsProvider } from './contexts/JobNotificationsContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { OnboardingTour } from './components/onboarding';
import OnboardingPage from './pages/OnboardingPage';
import { IMPORTS_RESULT_ROUTE_PREFIX, IMPORTS_REVIEW_ROUTE_PREFIX, IMPORTS_ROUTE, STOCK_IMPORTS_RESULT_ROUTE_PREFIX, STOCK_IMPORTS_REVIEW_ROUTE_PREFIX, STOCK_IMPORTS_ROUTE, STOCK_INSTRUMENT_ROUTE_PREFIX, STOCK_POSITIONS_ROUTE, STOCK_TRANSACTIONS_ROUTE, STOCKS_ROUTE_PREFIX } from './constants/routes';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  return (
    <footer className="py-6 mt-auto text-center text-gray-500 text-sm">
      <p>© {currentYear} MoonReady. All Rights Reserved.</p>
    </footer>
  );
};

const App = () => (
  <ApolloProvider client={client}>
    <AuthProvider>
      <PortfolioProvider>
        <OnboardingProvider>
        <PositionProvider>
          <ThemeProvider>
          <TradingViewProvider>
            <AuthWrapper>
            <ImportModalProvider>
              <Router>
                <JobNotificationsProvider>
                <OnboardingTour />
                <div className="flex flex-col min-h-screen">
                  <div className="sticky top-0 z-40 bg-[rgb(var(--color-background))]">
                    <div className="flex w-full justify-center px-2 sm:px-5 py-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      <div className="mx-auto min-w-max text-xs text-gray-700 dark:text-gray-300">
                        <MarketIndicators />
                      </div>
                    </div>
                    <Header />
                  </div>
                  <main className="flex-grow px-2 pt-2">
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path={STOCKS_ROUTE_PREFIX} element={<StocksDashboardPage />} />
                      <Route path={STOCK_POSITIONS_ROUTE} element={<StockPositionsPage />} />
                      <Route path={STOCK_ALLOCATIONS_ROUTE} element={<StockAllocationPlannerPage />} />
                      <Route path={STOCK_TRANSACTIONS_ROUTE} element={<TransactionsPage key="stock" assetKind="stock" />} />
                      <Route path={STOCK_IMPORTS_ROUTE} element={<ImportsPage assetKind="stock" />} />
                      <Route path={`${STOCK_IMPORTS_REVIEW_ROUTE_PREFIX}/:jobId`} element={<ImportsReviewPage assetKind="stock" />} />
                      <Route path={`${STOCK_IMPORTS_RESULT_ROUTE_PREFIX}/:jobId`} element={<ImportResultPage assetKind="stock" />} />
                      <Route path={`${STOCK_INSTRUMENT_ROUTE_PREFIX}/:stockIdentifier`} element={<StockPage />} />
                      <Route path="/positions" element={<PositionsPage />} />
                      <Route path="/transactions" element={<TransactionsPage key="crypto" />} />
                      <Route path={IMPORTS_ROUTE} element={<ImportsPage />} />
                      <Route path={`${IMPORTS_REVIEW_ROUTE_PREFIX}/:jobId`} element={<ImportsReviewPage />} />
                      <Route path={`${IMPORTS_RESULT_ROUTE_PREFIX}/:jobId`} element={<ImportResultPage />} />
                      <Route path="/transactions/export" element={<ExportPage />} />
                      <Route path="/c/:coingeckoSlug" element={<TokenPage />} />
                      <Route path="/allocation-planner" element={<AllocationPlannerPage />} />
                      <Route path="/exits" element={<ExitsPage />} />
                      <Route path="/strategies" element={<StrategiesPage />} />
                      <Route path="/groups" element={<GroupsPage />} />
                      <Route path="/tags" element={<TagsPage />} />
                      <Route path="/onboarding" element={<OnboardingPage />} />
                      {/* <Route path="/tokens" element={<TokensPage />} /> */}
                      <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
                      <Route path="/settings/security" element={<AccountSecurityPage />} />
                      <Route path="/settings/api-keys" element={<ApiKeysPage />} />
                      <Route path="/auth/reset" element={<PasswordResetPage />} />
                      <Route path="/lending-check" element={<LendingCheckPage />} />
                    </Routes>
                  </main>
                  <Footer />
                </div>
                <Toaster 
                  position="top-right"
                  toastOptions={{
                    // Default options for all toasts
                    duration: 3000,
                    style: {
                      background: 'var(--toast-bg, #374151)',
                      color: 'var(--toast-color, #fff)',
                      border: '1px solid var(--toast-border, #4B5563)',
                      borderRadius: '0.5rem',
                      fontSize: '14px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    },
                    // Success toasts
                    success: {
                      style: {
                        background: 'var(--toast-success-bg, #065F46)',
                        color: 'var(--toast-success-color, #D1FAE5)',
                        border: '1px solid var(--toast-success-border, #059669)',
                      },
                      iconTheme: {
                        primary: '#10B981',
                        secondary: '#D1FAE5',
                      },
                    },
                    // Error toasts
                    error: {
                      style: {
                        background: 'var(--toast-error-bg, #7F1D1D)',
                        color: 'var(--toast-error-color, #FEE2E2)',
                        border: '1px solid var(--toast-error-border, #DC2626)',
                      },
                      iconTheme: {
                        primary: '#EF4444',
                        secondary: '#FEE2E2',
                      },
                    },
                  }}
                />
                </JobNotificationsProvider>
              </Router>
            </ImportModalProvider>
            </AuthWrapper>
          </TradingViewProvider>
        </ThemeProvider>
        </PositionProvider>
        </OnboardingProvider>
      </PortfolioProvider>
    </AuthProvider>
  </ApolloProvider>
);

export default App;
